import React, { useCallback, useEffect, useRef } from "react";
import { imgSrc } from "@/lib/img";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronLeft, ChevronRight, X } from "lucide-react";

// Visore a schermo intero per le gallery: frecce, tastiera, swipe e contatore.
export function Lightbox({ images = [], index, alt = "", onClose, onIndex, testId = "lightbox" }) {
  const open = index !== null && index !== undefined && images.length > 0;
  const touchX = useRef(null);

  const step = useCallback(
    (dir) => {
      if (!images.length) return;
      onIndex((index + dir + images.length) % images.length);
    },
    [images.length, index, onIndex]
  );

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") step(1);
      if (e.key === "ArrowLeft") step(-1);
    };
    window.addEventListener("keydown", onKey);
    const prev = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = prev;
    };
  }, [open, onClose, step]);

  const onTouchEnd = (e) => {
    if (touchX.current === null) return;
    const dx = e.changedTouches[0].clientX - touchX.current;
    touchX.current = null;
    if (Math.abs(dx) > 50) step(dx < 0 ? 1 : -1);
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[95] bg-obsidian/95 backdrop-blur-md flex flex-col"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={onClose}
          data-testid={testId}
        >
          <div className="flex items-center justify-between px-4 sm:px-8 h-16 shrink-0">
            <span className="font-mono text-[10px] uppercase tracking-[0.25em] text-sand" data-testid={`${testId}-counter`}>
              {index + 1} / {images.length}
            </span>
            <button
              type="button"
              onClick={onClose}
              aria-label="Chiudi"
              className="text-cream2 hover:text-copper transition-colors"
              data-testid={`${testId}-close`}
            >
              <X className="h-7 w-7" />
            </button>
          </div>

          <div
            className="flex-1 flex items-center justify-center px-2 sm:px-6 pb-6"
            onClick={(e) => e.stopPropagation()}
            onTouchStart={(e) => { touchX.current = e.touches[0].clientX; }}
            onTouchEnd={onTouchEnd}
          >
            {images.length > 1 && (
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="Foto precedente"
                className="hidden sm:flex h-12 w-12 shrink-0 border border-copper/40 items-center justify-center text-copper hover:bg-copper hover:text-obsidian transition-colors"
                data-testid={`${testId}-prev`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
            )}
            <motion.img
              key={index}
              src={imgSrc(images[index], 2000)}
              alt={alt ? `${alt} — ${index + 1}` : ""}
              className="mx-auto max-h-[78vh] max-w-full object-contain select-none"
              initial={{ opacity: 0, scale: 0.98 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.35 }}
              data-testid={`${testId}-image`}
            />
            {images.length > 1 && (
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="Foto successiva"
                className="hidden sm:flex h-12 w-12 shrink-0 border border-copper/40 items-center justify-center text-copper hover:bg-copper hover:text-obsidian transition-colors"
                data-testid={`${testId}-next`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            )}
          </div>

          {images.length > 1 && (
            <div className="sm:hidden flex items-center justify-center gap-4 pb-8" onClick={(e) => e.stopPropagation()}>
              <button
                type="button"
                onClick={() => step(-1)}
                aria-label="Foto precedente"
                className="h-12 w-12 border border-copper/40 flex items-center justify-center text-copper"
                data-testid={`${testId}-prev-mobile`}
              >
                <ChevronLeft className="h-5 w-5" />
              </button>
              <button
                type="button"
                onClick={() => step(1)}
                aria-label="Foto successiva"
                className="h-12 w-12 border border-copper/40 flex items-center justify-center text-copper"
                data-testid={`${testId}-next-mobile`}
              >
                <ChevronRight className="h-5 w-5" />
              </button>
            </div>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  );
}

export default Lightbox;
